import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { cognitoVerifyEmail } from '../login/cognito';
import { Button } from '../../components/Button';

const COGNITO_REGION = 'eu-central-1';

export interface EmailVerificationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  newEmail: string;
  accessToken: string;
  onEmailVerified: () => void;
}

export default function EmailVerificationModal(props: EmailVerificationModalProps) {
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState('');

  const handleVerification = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    if (verificationCode.length !== 6) {
      setError('Verification code must be 6 digits');
      return;
    }

    setIsVerifying(true);
    setError('');

    const result = await cognitoVerifyEmail(COGNITO_REGION, props.accessToken, verificationCode);

    if (result.success) {
      setIsVerified(true);
      setTimeout(() => {
        props.onEmailVerified();
        props.setOpen(false);
      }, 2000);
    } else {
      setError(result.error || 'Invalid verification code. Please try again.');
    }

    setIsVerifying(false);
  };

  const handleClose = () => {
    setVerificationCode('');
    setError('');
    setIsVerified(false);
    props.setOpen(false);
  };

  return (
    <Dialog open={props.open} onClose={handleClose} className="relative z-10">
      {/* DialogBackdrop */}
      <div className="fixed inset-0 bg-neutral-dark/75 transition-opacity" aria-hidden="true"></div>

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <Dialog.Panel
            className="relative transform overflow-hidden rounded-lg bg-surface px-4 pt-5 pb-4 text-left
            shadow-xl sm:my-8 sm:w-full sm:max-w-sm sm:p-6"
          >
            <div>
              {isVerified && (
                <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-success-subtle">
                  <CheckIcon aria-hidden="true" className="size-6 text-success" />
                </div>
              )}
              <div className="mt-3 text-center sm:mt-5">
                <Dialog.Title as="h3" className="text-base font-semibold text-main">
                  {isVerified ? 'Email Verified!' : 'Verify New Email'}
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-muted">
                    {isVerified
                      ? 'Your email has been successfully updated.'
                      : `We've sent a verification code to ${props.newEmail}. Please enter the code below.`}
                  </p>
                </div>
              </div>
            </div>

            {!isVerified && (
              <div className="mt-4">
                <div>
                  <label
                    htmlFor="verification-code"
                    className="block text-sm font-medium text-main"
                  >
                    Verification Code
                  </label>
                  <div className="mt-1">
                    <input
                      id="verification-code"
                      name="verification-code"
                      type="text"
                      maxLength={6}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      disabled={isVerifying}
                      placeholder="Enter 6-digit code"
                      className="block w-full rounded-md bg-surface px-3 py-1.5 text-base text-main
                      outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-disabled focus:outline
                      focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                    />
                  </div>
                </div>
                {error && <p className="mt-2 text-sm text-error">{error}</p>}
                <div className="mt-5 sm:mt-6">
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleVerification}
                    disabled={isVerifying || verificationCode.length !== 6}
                    loading={isVerifying}
                    fullWidth
                  >
                    {isVerifying ? 'Verifying...' : 'Verify Email'}
                  </Button>
                </div>
                <div className="mt-3 text-center">
                  <p className="text-xs text-muted">
                    Didn't receive the code? Check your spam folder or try again.
                  </p>
                </div>
              </div>
            )}

            {isVerified && (
              <div className="mt-5 sm:mt-6">
                <Button variant="primary" size="md" onClick={handleClose} fullWidth>
                  Continue
                </Button>
              </div>
            )}
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
